import './adRotator.css';
import { useState, useEffect, useContext, memo, useRef } from 'react';
import { AuthContext } from '../../context/AuthContext';
import Ad from '../ad/Ad';
import arePropsEqual from '../../arePropsEqual';
import axiosConn from '../../axiosConn';
import { useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';

const getPlayerToken = (player) => player?.token || '';

const buildOptionalAuthHeaders = (player) => {
  const token = getPlayerToken(player);
  return token
    ? {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    : {};
};

const chooseRandomIndex = (items = []) => {
  if (!Array.isArray(items) || items.length <= 0) return 0;
  return Math.floor(Math.random() * items.length);
};

export default memo(function AdRotator({
  surfaceKey = 'legacy',
  mode = 'legacy',
  renderEmpty,
  onHitLabSongAdClick,
}) {
  const [randomNum, setRandomNum] = useState(0);
  const [adItems, setAdItems] = useState([]);
  const { player } = useContext(AuthContext);
  const adItemsRef = useRef(adItems);
  const location = useLocation();
  const isHitLabSongMode = mode === 'hitlab-song';

  useEffect(() => {
    adItemsRef.current = adItems;
  }, [adItems]);

  const getAds = async () => {
    try {
      if (isHitLabSongMode) {
        const res = await axiosConn.get('hitlab/song-ads/rotator', {
          ...buildOptionalAuthHeaders(player),
          params: { surface: surfaceKey },
        });
        return res?.data?.data?.items || [];
      }
      const token = getPlayerToken(player);
      if (!token) return [];
      const res = await axiosConn.get('ads/rotation', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: { surface: surfaceKey },
      });
      return res?.data?.data?.data || [];
    } catch (err) {
      console.log(err);
      // Handle error here
      return [];
    }
  };

  const { isLoading, data, status } = useQuery(
    ['getAds', surfaceKey, mode],
    getAds,
    {
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  );

  useEffect(() => {
    let mounted = true;

    const handleVisibilityChange = () => {
      if (!document.hidden && mounted && adItemsRef.current) {
        setRandomNum(chooseRandomIndex(adItemsRef.current));
      }
    };

    if (status === 'success' && mounted) {
      setAdItems(Array.isArray(data) ? data : []);
      handleVisibilityChange();
    }

    window.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      mounted = false;
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [status, data]);

  useEffect(() => {
    if (status === 'success' && data) {
      setRandomNum(chooseRandomIndex(data));
    }
  }, [location.pathname, status, data]);

  const hasRenderableAd =
    adItems && adItems[randomNum] && adItems.length > randomNum;

  return (
    <div className="adRotatorSelfCentering">
      <div
        className={[
          'adRotatorContainerElement2',
          isHitLabSongMode ? 'adRotatorContainerElement2--hitlab-song' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        id="adRotatorContainerElement2"
      >
        {isLoading ? (
          <div>Loading...</div>
        ) : !hasRenderableAd && typeof renderEmpty === 'function' ? (
          renderEmpty()
        ) : (
          hasRenderableAd && (
            <Ad
              ad={adItems[randomNum]}
              surfaceKey={surfaceKey}
              mode={mode}
              onHitLabSongAdClick={onHitLabSongAdClick}
            />
          )
        )}
      </div>
    </div>
  );
}, arePropsEqual);
